/**
 * The public site's numbers and capability names, in one place.
 *
 * The site used to carry its own copy of every figure on every page, and they
 * drifted: engagement was 35–45% on the homepage, 45% in one case study, 35%
 * "higher" in another and 19.25% in the seed deck. An investor who read the
 * deck and then the site saw three different answers to one question.
 *
 * Every figure below is the one the October 2026 seed deck uses, with the
 * definition a reader needs to compare it against anything else. Change a
 * number here and it changes everywhere; never type one into a page.
 */

export interface Metric {
  value: string;
  label: string;
  /** What the number counts, in a sentence a skeptical reader would accept. */
  definition: string;
}

/** Cohort reach across four CBO federations, vs. the CHCS Medicaid SMS baseline. */
export const ENGAGEMENT: Metric = {
  value: '19.25%',
  label: 'Engagement rate',
  definition:
    'Share of the 955.6K-member Medi-Cal outreach cohort who engaged, pooled across four CBO federations.',
};

export const ENGAGEMENT_MULTIPLE: Metric = {
  value: '12.8×',
  label: 'The CHCS benchmark',
  definition: 'Against the 1.5% Center for Health Care Strategies Medicaid SMS baseline.',
};

export const BENCHMARK_BASELINE = 1.5;

/** Each federation's engagement rate. Labels are program types, not partner names. */
export const FEDERATION_ENGAGEMENT: { label: string; rate: number }[] = [
  { label: 'Statewide coverage initiative', rate: 9.0 },
  { label: 'Health plan incentive program', rate: 12.4 },
  { label: 'ECM provider network', rate: 15.2 },
  { label: 'Promotora-led network', rate: 20.1 },
];

export const LANGUAGE_EQUITY: Metric = {
  value: '38.6%',
  label: 'Reached in a language other than English',
  definition: '368K members, about twice the 19.5% California baseline (Pew 2024).',
};

export const ER_REDUCTION: Metric = {
  value: '~9%',
  label: 'ER reduction',
  definition: 'Change in emergency department visits for the engaged cohort.',
};

export const PROOF_STRIP: Metric[] = [
  { value: '955.6K', label: 'Members in cohort', definition: 'Medi-Cal members across four CBO federations.' },
  { value: '5.12M', label: 'Outreach touchpoints', definition: 'Messages, calls and visits since inception.' },
  { value: '100+', label: 'CBOs on the platform', definition: 'Community organizations, through four federations.' },
  { value: '35K+', label: 'Enrollments & renewals', definition: 'Medi-Cal enrollments and re-enrollments.' },
  { value: '893', label: 'Validated ECM enrollments', definition: 'Enhanced Care Management enrollments a plan confirmed.' },
  { value: '481K+', label: 'Care navigations', definition: 'Members connected to a service.' },
  { value: '$3M+', label: 'Revenue earned by CBOs', definition: 'Multi-payer revenue the community organizations on the platform were paid.' },
];

/**
 * Members served across every Trust and WeaveIn program since 2018, the older
 * all-programs figure. The homepage leads with it (user decision 2026-11-02);
 * the cohort figures above stay the measured, benchmarked numbers.
 */
export const MEMBERS_SERVED: Metric = {
  value: '2M+',
  label: 'Members served',
  definition: 'Community members reached across Trust and WeaveIn programs since 2018.',
};

export const CBO_COUNT = '100+';
export const LANGUAGES = '110+';

/**
 * California Medi-Cal plans with plan rules held as data, as production
 * reported it on 2026-11-02 (active plans with rule rows). The live figure
 * comes from /api/public/funder-coverage; this is only the fallback.
 */
export const CA_PLANS_MAPPED = 23;

/**
 * Of those, the plans with rules specific to that plan (the ECM U1 modifier
 * and a Community Supports menu) rather than only the generic and
 * California-wide rows every plan carries. Production, 2026-11-02.
 */
export const CA_PLANS_PLAN_SPECIFIC = 6;

/** The deck's unit economics: one ECM member, one month. Illustrative; validated across the top five accounts. */
export const UNIT_ECONOMICS = {
  contractedRate: 350,
  careDelivery: 175,
  interfaceAdmin: 65,
  reworkDenials: 30,
  neverSubmitted: 40,
};

export type CapabilityStatus = 'Live' | 'Pilot' | 'Planned';

export interface Capability {
  key: string;
  name: string;
  /** Plain words, for the people who do the work. */
  plain: string;
  /** Who signs off, because agents draft and people decide. */
  approves: string;
  /** The integration surface, for engineers. */
  spec: string;
  status: CapabilityStatus;
}

/**
 * The six capabilities from the seed deck, in the deck's order. Platform,
 * Developers and every audience page describe the product with these and no
 * other list.
 */
export const CAPABILITIES: Capability[] = [
  {
    key: 'trust',
    name: 'Trust layer',
    plain: 'Members hear from the community staff they already trust, by text, WhatsApp, voice or in person, in 110+ languages. Consent is recorded on every exchange.',
    approves: 'The CHW or promotora owns the relationship',
    spec: 'Messaging, voice and consent APIs; SMS, WhatsApp and voice channels; consent recorded per exchange.',
    status: 'Live',
  },
  {
    key: 'eligibility',
    name: 'Eligibility & enrollment',
    plain: 'Coverage is checked before anyone is sent anywhere, so a referral does not fail at the front desk. Each plan’s member file is read in its own format.',
    approves: 'Ops sets what each funder gets',
    spec: 'X12 270/271 eligibility through a national clearinghouse; plan member files (MIF) read through layouts stored as data.',
    status: 'Live',
  },
  {
    key: 'encounter',
    name: 'Encounter capture',
    plain: 'The worker records the visit once, where it happens. An agent drafts the note and the service codes; the worker confirms them.',
    approves: 'The CHW confirms the note',
    spec: 'Encounter and progress-note APIs, FHIR R4 resources, a webhook when a note is signed.',
    status: 'Live',
  },
  {
    key: 'rules',
    name: 'Funder rules & submission',
    plain: 'Each funder’s service menu, rates, modifiers and filing window are held as data, not code. The record goes to each funder the way that funder takes it.',
    approves: 'Billing lead approves exceptions; a supervisor signs off',
    spec: 'Effective-dated rule rows per payer and program; 837P through a clearinghouse, plan workbooks and portal tasks from one submission record.',
    status: 'Live',
  },
  {
    key: 'disbursement',
    name: 'Disbursement',
    plain: 'Member incentives and provider payments go out on Trust cards, tied to the milestone that earned them.',
    approves: 'Finance releases each payout run',
    spec: 'Partner API v1 disbursements and a disbursement.status_changed webhook.',
    status: 'Live',
  },
  {
    key: 'reconciliation',
    name: 'Reconciliation',
    plain: 'Each payment is matched back to the visit that earned it. What does not match goes to a person, not a spreadsheet.',
    approves: 'Finance reviews exceptions',
    spec: 'X12 835 remittance parsing and claim matching; period close per funder.',
    // Pilot until production remittance volume is confirmed; the parser and
    // matching are built, the volume is not yet something we can point to.
    status: 'Pilot',
  },
];

export const CONTACT_TOPICS = [
  { value: 'deliver', label: 'I deliver care (community organization or hub)' },
  { value: 'fund', label: 'I fund care (health plan, county or state)' },
  { value: 'build', label: 'I build products (partner or developer)' },
  { value: 'employer', label: 'I’m an employer' },
  { value: 'sandbox', label: 'API sandbox access' },
  { value: 'guide', label: 'Request a guide or playbook' },
  { value: 'general', label: 'Something else' },
] as const;

export type ContactTopic = (typeof CONTACT_TOPICS)[number]['value'];

/** A link into the contact form with the topic pre-selected. */
export const contactHref = (topic: ContactTopic, note?: string) =>
  `/contact?topic=${topic}${note ? `&note=${encodeURIComponent(note)}` : ''}`;
